import React, { useState, useEffect } from "react";
import { Col, Input, Row, Button, Table, Form, message, Space, Popconfirm } from "antd";
import { ReactComponent as SaveIcon } from "../../../assets/save-icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete-icon.svg";
import { ReactComponent as CrossIcon } from "../../../assets/cross-icon.svg";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { updateLab, deleteStockNotificationRecipient } from "../redux/actions";
import SpinnerComponent from "../../../components/spinner";
import "./labSections.css";

const StockNotificationRecipientsSection = ({ 
  isLabUpdate, 
  canUpdate, 
  labId,
  initialRecipients = [],
  isLabSelected = false
}) => {
  const [recipients, setRecipients] = useState(initialRecipients);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [form] = Form.useForm();
  
  const dispatch = useDispatch();
  const { currentLabObj, updateLabLoading, deleteRecipientLoading } = useSelector((state) => state.labsSetupReducer);

  useEffect(() => {
    // Map API response format to component format
    const mappedRecipients = initialRecipients.map((recipient, index) => ({
      id: index + 1, // Generate ID for existing recipients
      fullName: recipient.full_name || '',
      designation: recipient.designation || '',
      email: recipient.email || '',
      recipientIndex: index,
      isNew: false,
      isAdd: false
    }));
    setRecipients(mappedRecipients);
  }, [initialRecipients]);

  useEffect(() => {
    setLoading(updateLabLoading || deleteRecipientLoading);
  }, [updateLabLoading, deleteRecipientLoading]);

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      fullName: record.fullName || '',
      designation: record.designation || '',
      email: record.email || '',
    });
    setEditingKey(record.id);
  };

  const cancel = (record) => {
    form.resetFields();
    setEditingKey('');
    if (record?.isAdd) {
      setRecipients(recipients.filter(r => r.id !== record.id));
    }
  };

  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const newRecipients = [...recipients];
      const index = newRecipients.findIndex(item => id === item.id);
      
      if (index > -1) {
        const item = newRecipients[index];
        
        // Check if email already exists
        const emailExists = newRecipients.some(
          r => r.id !== id && r.email.toLowerCase() === row.email.toLowerCase()
        );
        
        if (emailExists) {
          message.error("Email already exists!");
          return;
        }
        
        // Update the recipient
        const updatedItem = { 
          ...item, 
          ...row,
          isAdd: false,
          isNew: false
        };
        
        newRecipients[index] = updatedItem;
        setRecipients(newRecipients);
        setEditingKey('');

        // Call handleSave with the updated recipients array
        handleSave(newRecipients);
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const handleDeleteRecipient = (record) => {
    // If it's a new recipient that hasn't been saved yet
    if (record.isNew) {
      setRecipients(recipients.filter(r => r.id !== record.id));
      return;
    }
    
    dispatch(deleteStockNotificationRecipient({
      labId: labId,
      index: record.recipientIndex
    }))
  };

  const handleAddRecipient = () => {
    if (editingKey !== "") return;
    
    const newRecipient = {
      id: -Date.now(), // Temporary ID for new recipients
      fullName: '',
      designation: '',
      email: '',
      isAdd: true,
      isNew: true
    };
    setRecipients([...recipients, newRecipient]);
    edit(newRecipient);
  };

  const handleSave = async (updatedRecipients = null) => {
    try {
      // Use provided recipients array or fall back to state
      const recipientsToSave = updatedRecipients || recipients;
      
      // Format recipients data according to the specified structure
      const stockNotificationsRecipients = recipientsToSave
        .filter(recipient => !recipient.isNew) // Only include saved recipients
        .map(recipient => ({
          full_name: recipient.fullName,
          email: recipient.email,
          ...(recipient.designation && { designation: recipient.designation }) // Only include designation if it exists
        }));

      // Create payload for lab update
      const payload = {
        id: currentLabObj.id,
        stockNotificationsRecipients
      };

      // Dispatch updateLab action
      dispatch(updateLab(payload));
      
      setEditingKey('');
    } catch (error) {
      message.error('Failed to update stock notification recipients');
    }
  };

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    let inputNode;
    
    if (dataIndex === 'fullName') {
      inputNode = <Input placeholder="Insert Full Name" />;
    } else if (dataIndex === 'designation') {
      inputNode = <Input placeholder="Insert Designation" />;
    } else if (dataIndex === 'email') {
      inputNode = <Input placeholder="Insert Email" />;
    }
    
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              { required: true, message: `Please input ${title}!` },
              ...(dataIndex === 'email' ? [
                {
                  type: 'email',
                  message: 'Please enter a valid email!',
                }
              ] : [])
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: "FULL NAME",
      dataIndex: "fullName",
      key: "fullName",
      editable: true,
      width: "33%",
    },
    {
      title: "DESIGNATION",
      dataIndex: "designation",
      key: "designation",
      editable: true,
      width: "33%",
    },
    {
      title: "EMAIL",
      dataIndex: "email",
      key: "email",
      editable: true,
      width: "34%",
    },
  ];
  
  // Add ACTION column only when in edit mode
  columns.push({
    title: "",
    key: "action",
    width: "20%",
    render: (_, record) => {
      const editable = isEditing(record);
      return editable ? (
        loading ? (
          <SpinnerComponent fontSize={14} />
        ) : (
          <Space size={20} align="center">
            <SaveIcon
              className="custom-icon"
              onClick={() => save(record.id)}
            />
            <Popconfirm title="Are you sure you want to cancel?"
              onConfirm={() => cancel(record)}
              okText="Yes"
              cancelText="No"
            >
              <CrossIcon
                style={{ height: "20px", width: "20px" }}
                className="custom-icon"
              />
            </Popconfirm>
          </Space>
        )
      ) : (
        <Space size={20} align="center">
          <EditIcon
            className={`custom-icon ${editingKey !== "" || !canUpdate ? "disable-icon" : ""}`}
            onClick={() => editingKey === "" && canUpdate && edit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this recipient?"
            onConfirm={() => handleDeleteRecipient(record)}
            okText="Yes"
            cancelText="No"
            disabled={editingKey !== "" || !canUpdate}
          >
            <DeleteIcon className={`custom-icon ${editingKey !== "" || !canUpdate ? "disable-icon" : ""}`} />
          </Popconfirm>
        </Space>
      );
    },
  });

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === 'email' ? 'email' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <>
      <Row>
        <Col span={24} className="heading-col">
          <Row align="middle" justify="space-between">
            <Col>
              <div>
                <span className="heading-span">Stock Notification Recipients</span>
              </div>
            </Col>
          </Row>
        </Col>
        <Col
          span={24}
          className={`user-details-section lab-details-section`}
        >
          <Row justify="center">
            <Col md={24} lg={23} xl={20} xxl={16}>
              <Form form={form} component={false}>
                <Table
                  components={{
                    body: {
                      cell: EditableCell,
                    },
                  }}
                  className="special-table recipients-table"
                  dataSource={recipients}
                  columns={mergedColumns}
                  rowClassName="editable-row"
                  pagination={false}
                  rowKey="id"
                  locale={{ emptyText: '' }}
                />
              </Form>
              <Row className="priority-add-row">
                <Col span={24}>
                  <Button
                    icon={<PlusOutlined />}
                    className="add-activity-btn"
                    onClick={handleAddRecipient}
                    disabled={!canUpdate || Boolean(editingKey) || (!isLabSelected && !isLabUpdate)}
                  >
                    EMAIL
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="empty-div"></div>
    </>
  );
};

export default StockNotificationRecipientsSection;
